import React, { Fragment } from "react";

// Types
import { default as FormType } from "../../types/Form";
import Question from "../../types/Question";

// Utils
import { snakeCase } from "lodash";

// Services
import ApiService from "../../services/ApiService";

// Components
import { IonButton, IonToast } from "@ionic/react";
import { withTranslation } from "react-i18next";
import BasicFormBuilderFields from "./BasicFormBuilderFields";
import FormBuilderField from "./FormBuilderField";
import FormDetails from "./FormDetails";

// Fontawesome
import { faAsterisk } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface FormProps {
  form: FormType;
  type?: string;
  showFormOverview?: boolean;
  allowAppending?: boolean;
  appendAnywhere?: boolean;
  allowEditing?: boolean;
  t?: any;
  autoUpdateSlug?: boolean;
  toggleForm?: (form: FormType) => void;
}

interface FormState {
  form: FormType | null;
  hasChanges: boolean;
  toastMessage: string | null;
}

class Form extends React.Component<FormProps, FormState> {
  toggleForm: (form: FormType) => void;
  hideRequiredColumn = false;

  constructor(props: any) {
    super(props);
    this.toggleForm = props.toggleForm;
    this.hideRequiredColumn = props.hideRequiredColumn;
    this.state = { form: null, hasChanges: false, toastMessage: null };
  }

  render() {
    const { form, hasChanges } = this.state;
    const { t } = this.props;

    if (!form) return <div>{t("loading")}...</div>;

    return (
      <div id={"form-list__form--" + form.id} className="form-list__form">
        {this.props.type === "basic" ? (
          <div className="form-list__form__header" style={{ display: "flex", justifyContent: "space-between", borderBottom: "2px solid #fff", paddingBottom: "10px" }}>
            <div>{t("field")}</div>
            <div>{t("visibility")}</div>
            {!this.hideRequiredColumn && (
              <div>
                <FontAwesomeIcon icon={faAsterisk} />
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "flex" }} className="form-list__form__title ion-justify-content-between">
            <h2 onClick={() => this.toggleForm?.(form)}>{form.name}</h2>
            {hasChanges && (
              <div>
                <IonButton color="primary" onClick={this.saveForm}>
                  {t("save")}
                </IonButton>
              </div>
            )}
          </div>
        )}
        {form.visible && (
          <>
            {this.props.showFormOverview && (
              <>
                <h3>{t("form")}</h3>
                <FormDetails form={form} />
                <h3>{t("questions")}</h3>
              </>
            )}
            <div className="form-list__form__fields">
              {form.questions.map((question: Question, key: number) => (
                <Fragment key={key}>
                  {this.props.type === "basic" ? (
                    <BasicFormBuilderFields
                      question={question}
                      handleChange={(e) => this.handleQuestionUpdate(e, question)}
                      handleDelete={() => this.handleDelete(key)}
                      hideRequiredColumn={this.hideRequiredColumn}
                      allowEditing={this.props.allowEditing || false}
                    />
                  ) : (
                    <FormBuilderField question={question} handleChange={(e) => this.handleQuestionUpdate(e, question)} handleDelete={() => this.handleDelete(key)} />
                  )}

                  {this.props.allowAppending && (this.props.appendAnywhere ? true : key === form?.questions.length - 1) && (
                    <IonButton expand="block" size="small" color="light" className="form-list__form__new" onClick={() => this.handleNewQuestion(key)}>
                      <FontAwesomeIcon icon={["far", "plus"]} />
                    </IonButton>
                  )}
                </Fragment>
              ))}
            </div>

            {hasChanges && (
              <div style={{ display: "flex" }} className="ion-justify-content-end">
                <IonButton color="primary" className="ion-float-end" onClick={this.saveForm}>
                  {t("saveChanges")}
                </IonButton>
              </div>
            )}
          </>
        )}

        <IonToast
          isOpen={this.state.toastMessage !== null}
          onDidDismiss={() => this.setState({ toastMessage: null })}
          position="top"
          message={this.state.toastMessage!}
          color="success"
          duration={1000}
        />
      </div>
    );
  }

  componentDidMount() {
    this.setState({ form: this.props.form });
  }

  componentDidUpdate(prevProps: Readonly<any>) {
    if (prevProps.form !== this.props.form) this.setState({ form: this.props.form });
  }

  handleQuestionUpdate = (e: CustomEvent | any, question: Question) => {
    const form: FormType = this.state.form!;

    if (form) {
      form.questions = form.questions.map((newQuestion) => {
        if (question === newQuestion) {
          const key = e.detail.name ? e.detail.name : e.target.name;

          // @ts-ignore
          newQuestion[key] = e.detail.value;

          // Handle slug inputs
          if (this.props.autoUpdateSlug && key === "name") {
            newQuestion.key = snakeCase(e.detail.value);
          }

          // Handle json inputs
          if (["attr", "options"].includes(key)) {
            try {
              // @ts-ignore
              newQuestion[key] = JSON.parse(e.detail.value);
            } catch {}
          }
        }

        return newQuestion;
      });

      this.setState({ form, hasChanges: this.props.allowEditing ? true : this.props.type !== "basic" });

      if (this.props.type === "basic" && (e.target.attributes.type?.value === "checkbox" || e.target.attributes.type?.value === "radio" || e.target.attributes?.role.value === "switch")) {
        this.saveForm();
      }
    }
  };

  handleNewQuestion = (key: number) => {
    const form = this.state.form!;

    form.questions.splice(key + 1, 0, {
      name: "New question",
      key: `__${(+new Date()).toString()}`,
      value: undefined,
      type: "text",
      status: "",
      description: "",
      rules: [],
    });

    this.setState({ form, hasChanges: true });
  };

  handleDelete = (key: number) => {
    const form = this.state.form!;
    const { t } = this.props;

    if (window.confirm(t("deleteQuestionConfirmation"))) {
      delete form.questions[key];
      this.setState({ form });
    }
  };

  saveForm = () => {
    const form: FormType = this.state.form!;
    const { t } = this.props;

    form.questions = form.questions.filter((question) => question);

    ApiService.put("forms/" + form.id, { questions: form.questions })
      .then((form: FormType) => {
        this.setState({ hasChanges: false, toastMessage: form.name + ` ${t("formHasBeenUpdated")} !` });
      })
      .catch((err) => {
        console.error(err);
        alert(t("updateFormError"));
      });
  };
}

export default withTranslation()(Form);
